import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  Typography,
  useTheme,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useMemo } from 'react';

import { ERROR_MESSAGES } from '../../../constants/oneLink';
import { IOneLinkErrors } from '../../../contexts/OneLinkSummaryProvider';
import { mapErrorObjectToErrorList } from '../../../utils/oneLink';

const StyledDialog = styled(Dialog)({
  '& .MuiPaper-root': {
    padding: 36,
  },
});

const StyledDialogTitle = styled(DialogTitle)({
  padding: 0,
});

const StyledDialogContent = styled(DialogContent)({
  paddingLeft: 0,
  paddingRight: 0,
  maxHeight: 500,
  overflowY: 'auto',
});

const StyledDialogActions = styled(DialogActions)({
  paddingLeft: 0,
  paddingRight: 0,
});

interface IProps {
  trigger: boolean;
  onClose: () => void;
  errors?: IOneLinkErrors;
}

export const ErrorInformationModal: React.FC<IProps> = ({ trigger, onClose, errors }) => {
  const theme = useTheme();

  const errorList = useMemo(() => mapErrorObjectToErrorList(errors), [errors]);

  const getErrorExplanations = (list: string[]) => {
    return list?.map((error) => {
      const errorText = ERROR_MESSAGES[error];
      if (errorText) {
        return { error, errorText };
      }
      return { error, errorText: '' };
    });
  };

  const errorsWithExplanations = getErrorExplanations(errorList);

  return (
    <StyledDialog open={trigger} onClose={onClose} fullWidth maxWidth="sm">
      <StyledDialogTitle variant="h5" fontWeight={theme.sass.fontWeightBold}>
        Confirm One Link Activation
      </StyledDialogTitle>
      <StyledDialogContent>
        <Typography sx={{ mb: 2, fontWeight: theme.sass.fontWeightBold, fontSize: 18 }}>
          One Link can't be activated due to the following issues. Please resolve them and try again.
        </Typography>
        {errorList && errorList?.length > 0 && (
          <List>
            {errorsWithExplanations.map(({ error, errorText }) => (
              <ListItem key={error} sx={{ border: `1px solid ${theme.sass.grayEbony}`, borderRadius: 1, mb: 1 }}>
                <ListItemText
                  primary={
                    <Typography variant="body1" fontWeight={theme.sass.fontWeightBold}>
                      {error}
                    </Typography>
                  }
                  secondary={<Typography variant="body2">{errorText}</Typography>}
                />
              </ListItem>
            ))}
          </List>
        )}
      </StyledDialogContent>
      <StyledDialogActions>
        <Button onClick={onClose} fullWidth variant="contained" sx={{ color: theme.sass.white }}>
          Got it!
        </Button>
      </StyledDialogActions>
    </StyledDialog>
  );
};
